import React from 'react';
import { RotateCcw, Clock, CreditCard, AlertTriangle, Shield, DollarSign, CheckCircle, XCircle, Phone, Mail, Info } from 'lucide-react';

const ReturnPolicy = () => {
  const returnRules = [
    {
      icon: <XCircle className="w-6 h-6" />,
      title: 'No Returns After Trip Starts',
      description: 'Once the trip begins, cancellations will not be accepted, and the remaining amount must be paid.',
      color: 'bg-red-100 text-red-600',
      type: 'restriction'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Pre-Trip Cancellation',
      description: 'Cancellations made before the trip starts are eligible for a refund, provided the request is made at least 2 hours in advance.',
      color: 'bg-green-100 text-green-600',
      type: 'allowed'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Processing Timeline',
      description: 'Refunds for eligible cancellations will be processed within 5-7 business days.',
      color: 'bg-blue-100 text-blue-600',
      type: 'info'
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'No-Show Policy',
      description: 'In case of a no-show or failure to collect the bike, no refund will be provided.',
      color: 'bg-orange-100 text-orange-600',
      type: 'restriction'
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: 'Early Returns',
      description: 'If the bike is returned earlier than the scheduled time, no partial refund will be given.',
      color: 'bg-yellow-100 text-yellow-600',
      type: 'restriction'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Damage Deductions',
      description: 'Any damages to the bike during the rental period will be deducted from the security deposit.',
      color: 'bg-purple-100 text-purple-600',
      type: 'info'
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Original Payment Method',
      description: 'Refunds will only be processed to the original payment method.',
      color: 'bg-indigo-100 text-indigo-600',
      type: 'info'
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'Policy Discretion',
      description: 'The company reserves the right to refuse refunds for reasons outside its policy.',
      color: 'bg-gray-100 text-gray-600',
      type: 'restriction'
    }
  ];

  const returnProcess = [
    {
      step: '1',
      title: 'Inspect the Bike',
      description: 'Before returning, check the bike for any damages. Document any issues with photos.',
      icon: '🔍'
    },
    {
      step: '2',
      title: 'Return on Time',
      description: 'Return the bike to the agreed location at or before the scheduled return time.',
      icon: '⏰'
    },
    {
      step: '3',
      title: 'Staff Inspection',
      description: 'Our team will inspect the bike for damages, fuel level, and overall condition.',
      icon: '✓'
    },
    {
      step: '4',
      title: 'Deposit Refund',
      description: 'Security deposit will be refunded after deducting any applicable charges within 3-5 days.',
      icon: '💰'
    }
  ];

  const commonScenarios = [
    {
      scenario: 'On-Time Return',
      description: 'Bike returned on time with no damages',
      outcome: 'Full security deposit refunded within 3-5 business days',
      icon: '✅',
      color: 'border-green-500 bg-green-50'
    },
    {
      scenario: 'Early Return',
      description: 'Bike returned before scheduled time',
      outcome: 'No partial refund for unused rental time',
      icon: '⚠️',
      color: 'border-yellow-500 bg-yellow-50'
    },
    {
      scenario: 'Late Return',
      description: 'Bike returned after scheduled time',
      outcome: 'Late fees apply: ₹200 for first 30 mins, then hourly rates',
      icon: '⏱️',
      color: 'border-orange-500 bg-orange-50'
    },
    {
      scenario: 'Damaged Bike',
      description: 'Bike returned with damages',
      outcome: 'Repair costs deducted from security deposit',
      icon: '🔧',
      color: 'border-red-500 bg-red-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <RotateCcw className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Return Policy
            </h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Understanding our bike return process and refund terms for a seamless experience
          </p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6 shadow-md">
          <div className="flex items-start space-x-4">
            <Info className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Before You Return
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Please ensure you return the bike to the designated location at the scheduled time. Inspect the vehicle for damages before returning and inform our staff of any issues. Your security deposit will be refunded after a thorough inspection, minus any applicable charges.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Return Process */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Return Process
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Follow these simple steps to ensure a smooth bike return experience
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {returnProcess.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              <div className="text-5xl mb-4 text-center">{item.icon}</div>
              <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 mx-auto font-bold text-lg">
                {item.step}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                {item.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Policy Rules */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Return Policy Terms
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Please review these important terms regarding bike returns and refunds
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {returnRules.map((rule, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`${rule.color} w-14 h-14 rounded-full flex items-center justify-center mb-4`}>
                  {rule.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {rule.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {rule.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Scenarios */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Common Return Scenarios
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Understand what happens in different return situations
        </p>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {commonScenarios.map((item, index) => (
            <div
              key={index}
              className={`${item.color} rounded-xl p-6 border-l-4 shadow-md hover:shadow-lg transition-shadow duration-300`}
            >
              <div className="flex items-start space-x-4">
                <span className="text-4xl flex-shrink-0">{item.icon}</span>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {item.scenario}
                  </h3>
                  <p className="text-sm text-gray-700 mb-3">
                    {item.description}
                  </p>
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-800">
                      <strong>Outcome:</strong> {item.outcome}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inspection Checklist */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Pre-Return Checklist
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <p className="text-gray-600 mb-6 text-center">
              Complete this checklist before returning your bike to avoid any disputes
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Fuel Level</h4>
                  <p className="text-xs text-gray-600">Return with same fuel level as pickup</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Physical Condition</h4>
                  <p className="text-xs text-gray-600">Check for scratches, dents, or damages</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Cleanliness</h4>
                  <p className="text-xs text-gray-600">Return bike in clean condition</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Accessories</h4>
                  <p className="text-xs text-gray-600">Return helmets and other provided items</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Documents</h4>
                  <p className="text-xs text-gray-600">Return all vehicle documents received</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">On-Time Return</h4>
                  <p className="text-xs text-gray-600">Return at or before scheduled time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Timeline */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Security Deposit Refund Timeline
        </h2>
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>
            
            {/* Timeline Items */}
            <div className="space-y-12">
              <div className="flex items-center md:justify-start">
                <div className="flex-1 md:text-right md:pr-8">
                  <div className="bg-white rounded-lg shadow-md p-6 inline-block">
                    <h3 className="font-semibold text-gray-900 mb-2">Bike Return</h3>
                    <p className="text-sm text-gray-600">Day 0: Return bike and complete inspection</p>
                  </div>
                </div>
                <div className="hidden md:block w-8 h-8 bg-blue-600 rounded-full border-4 border-white shadow-md relative z-10"></div>
                <div className="flex-1 md:pl-8"></div>
              </div>
              
              <div className="flex items-center md:justify-end">
                <div className="flex-1 md:pr-8"></div>
                <div className="hidden md:block w-8 h-8 bg-blue-600 rounded-full border-4 border-white shadow-md relative z-10"></div>
                <div className="flex-1 md:text-left md:pl-8">
                  <div className="bg-white rounded-lg shadow-md p-6 inline-block">
                    <h3 className="font-semibold text-gray-900 mb-2">Damage Assessment</h3>
                    <p className="text-sm text-gray-600">Day 1-2: Thorough inspection and cost calculation</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center md:justify-start">
                <div className="flex-1 md:text-right md:pr-8">
                  <div className="bg-white rounded-lg shadow-md p-6 inline-block">
                    <h3 className="font-semibold text-gray-900 mb-2">Refund Processing</h3>
                    <p className="text-sm text-gray-600">Day 3-5: Deposit refunded to original payment method</p>
                  </div>
                </div>
                <div className="hidden md:block w-8 h-8 bg-green-600 rounded-full border-4 border-white shadow-md relative z-10"></div>
                <div className="flex-1 md:pl-8"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Important Notes
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Late Returns:</span> For every 30 minutes delay beyond scheduled time, ₹200 will be charged. After 1 hour, regular hourly rates apply.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Damage Costs:</span> All repair costs are assessed by authorized service centers. You'll receive a detailed breakdown of charges.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Early Returns:</span> While we appreciate early returns, rental fees are non-refundable for unused time. Plan your booking duration carefully.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Documentation:</span> Take photos of the bike condition at pickup and return to avoid disputes about pre-existing damages.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Trip Cancellation:</span> Once your trip has started, no cancellations are accepted. Refunds are only possible for pre-trip cancellations made 2+ hours in advance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Need Help with Returns?</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Have questions about our return policy or need assistance with your refund? Contact our support team
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <a
              href="tel:9730736858"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Phone className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Call Us</div>
                <div className="text-lg font-semibold">9730736858</div>
              </div>
            </a>
            <a
              href="mailto:Vegatasbike00@gmail.com"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Email Us</div>
                <div className="text-lg font-semibold break-all">Vegatasbike00@gmail.com</div>
              </div>
            </a>
          </div>
          <div className="text-center mt-8">
            <p className="text-sm text-blue-200">
              Support Hours: Monday - Sunday, 9:00 AM - 8:00 PM IST
            </p>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">Last Updated: October 2025</p>
          <p>
            By renting a bike from Vegatas, you agree to comply with this return policy. Terms are subject to change without prior notice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicy;
